import React, { useCallback, useEffect, useState } from 'react';
import { Fab, Tooltip } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { useConnectChat } from 'src/hooks/useConnectChat';
import useAuth from 'src/hooks/useAuth';
import { Z_INDEX } from 'src/constants/zIndex';
import SupportCasePanel from 'src/components/SupportCase/SupportCasePanel';
import {
  onSupportCaseReady,
  onSupportChatRequest,
  publishSupportChatState,
  type SupportChatIntent,
} from './supportChatBridge';

/**
 * The support button (ENG-4137, decision 4A).
 *
 * One corner, two back ends. When `support_cases_enabled` is on — which is a global
 * flag, so it is on for everyone or no one — the button opens a **support case**: a
 * non-modal panel beside the work, which knows the page, the certification and the
 * user's scope. When it is off, the old Connect chat path runs exactly as it did, so
 * turning the flag off is the whole rollback.
 *
 * The Help and Resources "Report issue" / "Start chat" buttons still reach this
 * component through `supportChatBridge`; on the case path they open the panel, so
 * there is one launcher rather than two competing ones.
 */
export default function FloatingChatButton(): React.ReactElement | null {
  const { openChat, isLoaded, error, hasActiveChat, loading } =
    useConnectChat();
  // The user object lives in the auth context, not in the redux store — reading the
  // flag from the wrong place silently keeps every user on the old launcher.
  const { user } = useAuth();
  const supportCasesEnabled = Boolean(user?.supportCasesEnabled);
  const [casePanelOpen, setCasePanelOpen] = useState(false);
  const [caseId, setCaseId] = useState<number | null>(null);

  // This is the single mounted launcher, so it owns opening the surface on behalf of
  // the rest of the app. Subscribing above the early return keeps the listener
  // registered even while the FAB is hidden.
  useEffect(
    () =>
      onSupportChatRequest((intent?: SupportChatIntent) => {
        if (supportCasesEnabled) {
          setCasePanelOpen(true);
          return;
        }
        openChat(intent);
      }),
    [openChat, supportCasesEnabled],
  );

  /**
   * A scheduled run finished (ENG-4185).
   *
   * `NotificationContext` owns the one notification socket and dispatches this when it
   * sees the `support_case_ready` system event — so the panel opens on the case the run
   * produced without a second socket and without the user hunting for the answer.
   * Offline users get the same notification as a bell row and an email with the deep
   * link, which is why this is a nicety rather than the delivery mechanism.
   */
  useEffect(() => {
    if (!supportCasesEnabled) {
      return undefined;
    }
    return onSupportCaseReady((readyCaseId: number) => {
      setCaseId(readyCaseId);
      setCasePanelOpen(true);
    });
  }, [supportCasesEnabled]);

  // Mirror provisioning state back out so those remote triggers can show progress.
  useEffect(() => {
    publishSupportChatState({ pending: loading, error });
  }, [loading, error]);

  const handleClick = useCallback(() => {
    if (supportCasesEnabled) {
      // ENG-4251 (PA-09): opening is not a reset. The panel resumes the open case for
      // this page; discarding caseId here is what made a close/reopen lose everything.
      setCasePanelOpen(true);
      return;
    }
    openChat();
  }, [openChat, supportCasesEnabled]);

  const closePanel = useCallback(() => {
    // A UI dismissal, not a resolution: the panel stays mounted so the conversation
    // and any draft survive, and no case state changes.
    setCasePanelOpen(false);
  }, []);

  if (supportCasesEnabled) {
    return (
      <>
        <Tooltip title="Chat with the Pronto Assistant" placement="left">
          <Fab
            color="primary"
            size="medium"
            onClick={handleClick}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: Z_INDEX.MAX,
            }}
            aria-label="Open the Pronto Assistant"
          >
            <ChatIcon />
          </Fab>
        </Tooltip>
        <SupportCasePanel
          onClose={closePanel}
          open={casePanelOpen}
          initialCaseId={caseId}
        />
      </>
    );
  }

  // Hide the default FAB whenever the launcher, live widget, or minimized pill is
  // showing — those all occupy the exact same corner.
  if (!isLoaded || hasActiveChat) {
    return null;
  }

  return (
    <Tooltip
      title={error || 'Chat with Support'}
      placement="left"
      slotProps={{ popper: { style: { zIndex: Z_INDEX.MAX + 1 } } }}
    >
      <Fab
        color="primary"
        size="medium"
        onClick={handleClick}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: Z_INDEX.MAX,
        }}
      >
        <ChatIcon />
      </Fab>
    </Tooltip>
  );
}
